'use client';

import { AnimatePresence } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StudioAIProvider } from '../../context';
import { isAssistantDisabledPage } from '../constants';
import { usePersistedSelection } from '../hooks/use-persisted-selection';
import { ChatPanel } from './chat-panel';
import { FloatingInputBar } from './floating-input-bar';
import { PersistentAssistantButton } from './persistent-assistant-button';

type AssistantState = 'idle' | 'input' | 'panel' | 'minimized';

/**
 * Self-contained floating Studio AI assistant.
 */
export function PersistentAssistant() {
    const pathname = usePathname();
    const [state, setState] = useState<AssistantState>('input');
    const [initialMessage, setInitialMessage] = useState<string | null>(null);
    const [hasConversation, setHasConversation] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);

    const { selectedText, clearSelection } = usePersistedSelection();

    const isAllowedPage = useMemo(() => !isAssistantDisabledPage(pathname), [pathname]);

    const dismissedRef = useRef(false);

    useEffect(() => {
        if (!isAllowedPage) {
            if (state !== 'idle') setState('idle');
            return;
        }
        if (state === 'idle') {
            if (hasConversation) {
                setState('minimized');
            } else if (!dismissedRef.current) {
                setState('input');
            }
        }
    }, [isAllowedPage, state, hasConversation]);

    const handleButtonClick = useCallback(() => {
        dismissedRef.current = false;
        setState(hasConversation ? 'panel' : 'input');
    }, [hasConversation]);

    const handleInputSend = useCallback((message: string) => {
        setInitialMessage(message);
        setState('panel');
    }, []);

    const handleInputClose = useCallback(() => {
        dismissedRef.current = true;
        setState('idle');
        clearSelection();
    }, [clearSelection]);

    const closePanel = useCallback(() => {
        if (hasConversation) {
            setState('minimized');
        } else {
            dismissedRef.current = true;
            setState('idle');
        }
        setInitialMessage(null);
        setIsMaximized(false);
        clearSelection();
    }, [hasConversation, clearSelection]);

    const handleInitialMessageSent = useCallback(() => {
        setInitialMessage(null);
    }, []);

    const handleHasMessagesChange = useCallback((hasMessages: boolean) => {
        setHasConversation(hasMessages);
    }, []);

    const handleMaximizeToggle = useCallback(() => {
        setIsMaximized((prev) => !prev);
    }, []);

    // Full /dani mounts its own StudioAIProvider. Skip a second provider (duplicate
    // queries/effects) while the floating UI is hidden on disabled routes.
    if (!isAllowedPage) {
        return null;
    }

    return (
        <StudioAIProvider disableNavigation>
            <AnimatePresence mode="wait">
                {state === 'idle' && (
                    <PersistentAssistantButton key="button-bottom" onClick={handleButtonClick} />
                )}
                {state === 'input' && (
                    <FloatingInputBar
                        key="input"
                        onSend={handleInputSend}
                        onClose={handleInputClose}
                        onOpenPanel={() => setState('panel')}
                        selectedText={selectedText}
                    />
                )}
                {state === 'minimized' && (
                    <PersistentAssistantButton
                        key="button-right"
                        onClick={handleButtonClick}
                        position="right"
                    />
                )}
            </AnimatePresence>
            {state === 'panel' && (
                <div
                    data-persistent-assistant
                    className={
                        isMaximized
                            ? 'fixed inset-0 z-[100] h-[100dvh] w-[100dvw]'
                            : 'fixed right-0 z-50 h-full shrink-0 shadow-lg lg:relative lg:shadow-none'
                    }
                >
                    {/* Drag handle — grab the left border to resize (hidden when maximized) */}
                    {!isMaximized && (
                        <div className="cursor-colx-resize absolute left-0 top-0 z-10 h-full w-1.5 transition-colors hover:bg-primary/20" />
                    )}
                    <ChatPanel
                        onClose={closePanel}
                        selectedText={selectedText}
                        onSelectedTextClear={clearSelection}
                        initialMessage={initialMessage}
                        onInitialMessageSent={handleInitialMessageSent}
                        onHasMessagesChange={handleHasMessagesChange}
                        resizable
                        resizeStorageKey="persistent-assistant-width"
                        isMaximized={isMaximized}
                        onMaximizeToggle={handleMaximizeToggle}
                    />
                </div>
            )}
        </StudioAIProvider>
    );
}
